import { Component, AfterViewInit, AfterViewChecked, ElementRef, ViewChild, inject, Output, EventEmitter, OnDestroy, Input, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormGroup, FormControl } from '@angular/forms';
import * as d3 from 'd3';
import { StoreService } from '../../services/store.service';
import { BusinessLogicService } from '../../services/business-logic.service';
import { ApiResponse, ComponentContextMenuRequest, ComponentType, GraphNode, GraphLink, GraphRelationMeta, GraphNodeOption, RemainingFailureModeOption } from '../../models';

@Component({
  selector: 'app-graph-view',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './graph-view.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './graph-view.component.css'
})
export class GraphViewComponent implements AfterViewInit, AfterViewChecked, OnDestroy, OnChanges {
  @ViewChild('graphContainer') graphContainerRef?: ElementRef<HTMLDivElement>;
  @ViewChild('unconnectedSourceNodesContainer') private unconnectedSourceNodesContainer?: ElementRef<HTMLElement>;
  @ViewChild('unconnectedTargetNodesContainer') private unconnectedTargetNodesContainer?: ElementRef<HTMLElement>;

  @Input() nodes: GraphNode[] = [];
  @Input() links: GraphLink[] = [];
  @Input() meta: any; // Bound in template
  @Input() showUnlinkedComponents = false;
  @Input() focusedComponentDesignator: string | null = null;

  @Output() nodeClicked = new EventEmitter<string>();
  @Output() linkCreated = new EventEmitter<{ source_id: string; target_id: string; rel_type: string }>();
  @Output() linkDeleted = new EventEmitter<{ source_id: string; target_id: string; rel_type: string }>();
  @Output() showUnlinkedChanged = new EventEmitter<void>();
  @Output() failureInstanceRequested = new EventEmitter<{ componentDesignator: string; mode: RemainingFailureModeOption }>();
  @Output() failureEffectCreated = new EventEmitter<{ name: string; system_effect_id: string; id?: string }>();
  @Output() componentContextMenuRequested = new EventEmitter<ComponentContextMenuRequest>();

  public readonly store = inject(StoreService);
  public readonly businessLogic = inject(BusinessLogicService);

  private graphPendingInit = false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private graphSimulation: any = null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private graphNodeSelection: any = null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private graphSvg: any = null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private graphZoomBehavior: any = null;
  private graphFocusTimer: number | null = null;

  graphLinkMode = false;
  graphLinkSource: GraphNode | null = null;
  graphLinkRelType = 'CAUSES';
  graphQuickCreatePane: 'source' | 'target' | null = null;

  readonly graphQcFailureEffectForm = new FormGroup({
    id: new FormControl('', { nonNullable: true }),
    name: new FormControl('', { nonNullable: true }),
    systemEffectId: new FormControl('', { nonNullable: true })
  });
  readonly graphQcSystemEffectForm = new FormGroup({
    id: new FormControl('', { nonNullable: true }),
    name: new FormControl('', { nonNullable: true }),
    safetyGoalId: new FormControl('', { nonNullable: true })
  });
  readonly graphQcSafetyGoalForm = new FormGroup({
    id: new FormControl('', { nonNullable: true }),
    description: new FormControl('', { nonNullable: true })
  });
  readonly graphQcSafetyMechanismForm = new FormGroup({
    smId: new FormControl('', { nonNullable: true }),
    name: new FormControl('', { nonNullable: true }),
    mechanismType: new FormControl('SPF', { nonNullable: true }),
    diagnosticCoverage: new FormControl(0.8, { nonNullable: true }),
    failureInstanceId: new FormControl('', { nonNullable: true })
  });

  // Proxy the signals used by the template
  readonly components = this.store.components;
  readonly componentTypes = this.store.componentTypes;
  readonly failureInstances = this.store.failureInstances;
  readonly failureEffects = this.store.failureEffects;
  readonly systemEffects = this.store.systemEffects;
  readonly safetyGoals = this.store.safetyGoals;
  readonly safetyMechanisms = this.store.safetyMechanisms;
  readonly coverageLinks = this.store.coverageLinks;
  readonly actionMessage = this.store.actionMessage;
  readonly error = this.store.error;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['focusedComponentDesignator']) {
      const designator = this.focusedComponentDesignator;

      if (designator && this.graphLinkMode) {
        // Use a timeout to allow the view to update if the link mode was just toggled.
        setTimeout(() => this.scrollToAndSelectComponent(designator), 100);
      } else {
        // Default behavior: focus on the main graph canvas
        this.scheduleFocusedComponentSync();
      }
    }

    if (changes['nodes'] || changes['links']) {
      // If the graph data changes from the parent, re-initialize.
      this.graphPendingInit = true;
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.initGraph();
    }, 100);
  }

  ngAfterViewChecked(): void {
    if (this.graphPendingInit && this.graphContainerRef?.nativeElement) {
      this.graphPendingInit = false;
      this.initGraph();
      this.applyGraphNodeIndicators();
    }
  }

  ngOnDestroy(): void {
    if (this.graphFocusTimer !== null) {
      window.clearTimeout(this.graphFocusTimer);
      this.graphFocusTimer = null;
    }

    if (this.graphSimulation) {
      this.graphSimulation.stop();
      this.graphSimulation = null;
    }
  }

  toggleGraphLinkMode(): void {
    this.graphLinkMode = !this.graphLinkMode;
    this.graphLinkSource = null;
    this.graphQuickCreatePane = null;
    this.actionMessage.set('');

    this.applyGraphNodeIndicators();
  }

  toggleShowUnlinkedComponents(): void {
    this.showUnlinkedChanged.emit();
  }

  onGraphRelTypeChanged(): void {
    this.graphLinkSource = null;
    this.graphQuickCreatePane = null;
    this.actionMessage.set('');

    this.applyGraphNodeIndicators();
  }

  graphQuickCreateEnabled(type: GraphNode['type']): boolean {
    return type === 'failure_effect' || type === 'system_effect' || type === 'safety_goal' || type === 'safety_mechanism';
  }

  openGraphPanelCreate(pane: 'source' | 'target'): void {
    this.graphQuickCreatePane = pane;
  }

  closeGraphPanelCreate(): void {
    this.graphQuickCreatePane = null;
  }

  submitGraphQcFailureEffect(): void {
    const v = this.graphQcFailureEffectForm.getRawValue();
    const payload: { name: string; system_effect_id: string; id?: string; } = {
      name: v.name.trim(),
      system_effect_id: v.systemEffectId.trim()
    };
    const requestedId = v.id.trim();
    if (requestedId) {
      payload.id = requestedId;
    }
    this.failureEffectCreated.emit(payload);
    this.graphQcFailureEffectForm.reset({ id: '', name: '', systemEffectId: '' });
    this.closeGraphPanelCreate();
  }

  submitGraphQcSystemEffect(): void {
  }

  submitGraphQcSafetyGoal(): void {
  }

  submitGraphQcSafetyMechanism(): void {
  }

  isGraphPanelNodeSelected(node: GraphNodeOption): boolean {
    return this.graphLinkSource?.id === node.id;
  }

  get graphSelectedComponentRemainingFailureModes(): RemainingFailureModeOption[] {
    if (this.graphLinkRelType !== 'EXPERIENCES' || this.graphLinkSource?.type !== 'component') {
      return [];
    }

    const componentDesignator = this.graphLinkSource.id;
    const linkedCodes = new Set(
      this.failureInstances()
        .filter((failureInstance) => failureInstance.component_designator === componentDesignator)
        .map((failureInstance) => failureInstance.failure_mode_code)
    );

    return this.businessLogic.getFailureModesForComponent(componentDesignator).map((mode) => ({
      code: mode.code,
      name: mode.name,
      isLinked: linkedCodes.has(mode.code),
    }));
  }

  get graphSelectedComponentRemainingFailureModeCount(): number {
    return this.graphSelectedComponentRemainingFailureModes.filter((mode) => !mode.isLinked).length;
  }

  createFailureInstanceFromMode(mode: RemainingFailureModeOption): void {
    if (this.graphLinkRelType !== 'EXPERIENCES' || this.graphLinkSource?.type !== 'component') {
      return;
    }

    if (mode.isLinked) {
      this.actionMessage.set(`Failure mode ${mode.code} is already linked for ${this.graphLinkSource.id}.`);
      return;
    }

    const componentDesignator = this.graphLinkSource.id;
    this.failureInstanceRequested.emit({ componentDesignator, mode });
  }

  onGraphPanelNodeContextMenu(event: MouseEvent, node: GraphNodeOption): void {
    this.requestComponentContextMenu(event, node);
  }

  private requestComponentContextMenu(event: MouseEvent, node: GraphNodeOption | GraphNode): void {
    if (node.type !== 'component') {
      return;
    }

    event.preventDefault();
    this.componentContextMenuRequested.emit({
      componentDesignator: node.id,
      clientX: event.clientX,
      clientY: event.clientY
    });
  }

  private scrollToAndSelectComponent(designator: string): void {
    const meta = this.graphRelationMeta;
    if (!meta) return;

    let container: HTMLElement | undefined;
    let elementId: string | undefined;

    // Determine which panel (source or target) might contain the component list
    if (meta.sourceType === 'component') {
      container = this.unconnectedSourceNodesContainer?.nativeElement;
      elementId = `unconnected-source-node-${designator}`;
    } else if (meta.targetType === 'component') {
      container = this.unconnectedTargetNodesContainer?.nativeElement;
      elementId = `unconnected-target-node-${designator}`;
    }

    if (!container || !elementId) {
      return; // The current link mode doesn't involve components in its side panels.
    }

    const element = container.querySelector<HTMLElement>(`#${elementId}`);

    if (element) {
      // Find the data object for the node to programmatically "click" it.
      // This reuses your existing selection logic.
      const nodeToSelect = this.getGraphNodeOptionsForType('component').find(n => n.id === designator);
      if (nodeToSelect) {
        this.handleNodeClick(nodeToSelect, 'panel');
      }

      // Scroll the element into the center of its container.
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }


  /**
   * This is the unified click handler for both nodes in the D3 graph
   * and nodes in the side panel lists. It orchestrates the logic for
   * selecting nodes for linking.
   */
  public handleNodeClick(node: GraphNodeOption, source: 'graph' | 'panel'): void {
    if (!this.graphLinkMode) {
      if (node.type === 'component') {
        this.nodeClicked.emit(node.id);
      }
      return;
    }

    // When a component is clicked in the side panel, always notify the parent
    // so the PDF viewer can focus on it.
    if (source === 'panel' && node.type === 'component') {
      this.nodeClicked.emit(node.id);
    }

    const relationMeta = this.getGraphRelationMeta(this.graphLinkRelType);
    if (!relationMeta) {
      this.error.set(`Unsupported relationship type: ${this.graphLinkRelType}`);
      return;
    }

    // Special case for the 'EXPERIENCES' relation, which has a unique workflow.
    if (relationMeta.graphRelType === 'HAS_FAILURE') {
      if (node.type === 'component') {
        // Toggle selection if the same component is clicked again
        this.graphLinkSource = (this.graphLinkSource?.id === node.id) ? null : { id: node.id, label: node.label, type: node.type };
        this.actionMessage.set(this.graphLinkSource ? `Source: ${node.id} — choose a failure mode in the right panel.` : 'Source deselected.');
      } else {
        this.actionMessage.set('For EXPERIENCES links, select a Component node as the source.');
      }
      this.applyGraphNodeIndicators();
      return;
    }

    // --- Standard Link Creation Logic ---

    if (this.graphLinkSource) {
      // Case 1: A source node is already selected.
      if (this.graphLinkSource.id === node.id) {
        // Subcase 1.1: Clicking the same source again deselects it.
        this.graphLinkSource = null;
        this.actionMessage.set('Source deselected.');
        this.applyGraphNodeIndicators();
        return;
      }

      if (node.type === relationMeta.targetType) {
        // Subcase 1.2: Clicking a valid target creates the link.
        const source = this.graphLinkSource;
        this.graphLinkSource = null; // Reset state before async operation
        this.linkCreated.emit({
          source_id: source.id,
          target_id: node.id,
          rel_type: this.graphLinkRelType
        });
        // The parent component will refresh data, causing a re-render.
        return;
      }

      if (node.type === relationMeta.sourceType) {
        // Subcase 1.3: Clicking another valid source switches the selection.
        this.graphLinkSource = { id: node.id, label: node.label, type: node.type };
        this.actionMessage.set(`Source changed to: ${node.id} — now click the target node.`);
        this.applyGraphNodeIndicators();
        return;
      }

      // Subcase 1.4: If we get here, it was an invalid click.
      this.actionMessage.set(`Invalid target. Select a ${this.typeLabel(relationMeta.targetType)} node, or a different source.`);
      return; // No state change, so no need to call applyGraphNodeIndicators.

    } else {
      // Case 2: No source node is selected yet.
      if (node.type === relationMeta.sourceType) {
        this.graphLinkSource = { id: node.id, label: node.label, type: node.type };
        this.actionMessage.set(`Source: ${node.id} — now click the target node.`);
      } else {
        this.actionMessage.set(`Select a ${this.typeLabel(relationMeta.sourceType)} node as the source.`);
      }
    }
    this.applyGraphNodeIndicators();
  }

  get graphRelationMeta(): GraphRelationMeta | null {
    return this.getGraphRelationMeta(this.graphLinkRelType);
  }

  get graphUnconnectedSourceNodes(): GraphNodeOption[] {
    const meta = this.graphRelationMeta;
    if (!this.graphLinkMode || !meta) {
      return [];
    }

    const nodes = this.getGraphNodeOptionsForType(meta.sourceType);
    const connectedSourceIds = this.getGraphConnectedEndpointIds(meta).sourceIds;
    return nodes.filter((node) => !connectedSourceIds.has(node.id));
  }

  get graphUnconnectedTargetNodes(): GraphNodeOption[] {
    const meta = this.graphRelationMeta;
    if (!this.graphLinkMode || !meta) {
      return [];
    }

    if (meta.graphRelType === 'HAS_FAILURE' && this.graphLinkSource?.type === 'component') {
      return [];
    }

    const nodes = this.getGraphNodeOptionsForType(meta.targetType);
    const connectedTargetIds = this.getGraphConnectedEndpointIds(meta).targetIds;
    return nodes.filter((node) => !connectedTargetIds.has(node.id));
  }

  private getGraphRelationMeta(relType: string): GraphRelationMeta | null {
    const map: Record<string, GraphRelationMeta> = {
      CAUSES: {
        sourceType: 'failure_instance',
        targetType: 'failure_effect',
        graphRelType: 'CAUSES'
      },
      LEADS_TO: {
        sourceType: 'failure_effect',
        targetType: 'system_effect',
        graphRelType: 'LEADS_TO'
      },
      VIOLATES: {
        sourceType: 'system_effect',
        targetType: 'safety_goal',
        graphRelType: 'VIOLATES'
      },
      COVERED_BY: {
        sourceType: 'failure_instance',
        targetType: 'safety_mechanism',
        graphRelType: 'COVERED_BY'
      },
      COVERED_BY_LATENT: {
        sourceType: 'failure_instance',
        targetType: 'safety_mechanism',
        graphRelType: 'COVERED_BY_LATENT'
      },
      EXPERIENCES: {
        sourceType: 'component',
        targetType: 'failure_instance',
        graphRelType: 'HAS_FAILURE'
      }
    };

    return map[relType] ?? null;
  }

  private getGraphNodeOptionsForType(type: GraphNode['type']): GraphNodeOption[] {
    const dedupe = new Set<string>();
    const toSortedUnique = (rows: GraphNodeOption[]): GraphNodeOption[] => {
      const uniqueRows: GraphNodeOption[] = [];
      for (const row of rows) {
        if (dedupe.has(row.id)) {
          continue;
        }
        dedupe.add(row.id);
        uniqueRows.push(row);
      }
      return uniqueRows.sort((a, b) => a.id.localeCompare(b.id));
    };

    if (type === 'component') {
      return toSortedUnique(this.components().map((component) => ({
        id: component.component_designator,
        label: component.component_designator,
        type
      })));
    }

    if (type === 'failure_instance') {
      return toSortedUnique(this.failureInstances().map((failureInstance) => ({
        id: failureInstance.id,
        label: failureInstance.name,
        type
      })));
    }

    if (type === 'failure_effect') {
      return toSortedUnique(this.failureEffects().map((effect) => ({
        id: effect.id,
        label: effect.name,
        type
      })));
    }

    if (type === 'system_effect') {
      return toSortedUnique(this.systemEffects().map((effect) => ({
        id: effect.id,
        label: effect.name,
        type
      })));
    }

    if (type === 'safety_goal') {
      return toSortedUnique(this.safetyGoals().map((goal) => ({
        id: goal.id,
        label: goal.description,
        type
      })));
    }

    return toSortedUnique(this.safetyMechanisms().map((mechanism) => ({
      id: mechanism.id,
      label: mechanism.name,
      type: 'safety_mechanism'
    })));
  }

  private getGraphConnectedEndpointIds(meta: GraphRelationMeta): { sourceIds: Set<string>; targetIds: Set<string> } {
    const sourceIds = new Set<string>();
    const targetIds = new Set<string>();

    if (meta.graphRelType === 'CAUSES') {
      for (const failureInstance of this.failureInstances()) {
        if (!failureInstance.failure_effect_id) {
          continue;
        }
        sourceIds.add(failureInstance.id);
        targetIds.add(failureInstance.failure_effect_id);
      }
      return { sourceIds, targetIds };
    }

    if (meta.graphRelType === 'LEADS_TO') {
      for (const effect of this.failureEffects()) {
        if (!effect.system_effect_id) {
          continue;
        }
        sourceIds.add(effect.id);
        targetIds.add(effect.system_effect_id);
      }
      return { sourceIds, targetIds };
    }

    if (meta.graphRelType === 'VIOLATES') {
      for (const effect of this.systemEffects()) {
        if (!effect.safety_goal_id) {
          continue;
        }
        sourceIds.add(effect.id);
        targetIds.add(effect.safety_goal_id);
      }
      return { sourceIds, targetIds };
    }

    if (meta.graphRelType === 'HAS_FAILURE') {
      for (const failureInstance of this.failureInstances()) {
        sourceIds.add(failureInstance.component_designator);
        targetIds.add(failureInstance.id);
      }
      return { sourceIds, targetIds };
    }

    if (meta.graphRelType === 'COVERED_BY' || meta.graphRelType === 'COVERED_BY_LATENT') {
      const selectedCoverageType = meta.graphRelType === 'COVERED_BY_LATENT' ? 'LATENT' : 'SPF';
      for (const link of this.coverageLinks()) {
        if (link.mechanism_type !== selectedCoverageType) {
          continue;
        }
        sourceIds.add(link.failure_instance_id);
        targetIds.add(link.safety_mechanism_id);
      }
    }
    return { sourceIds, targetIds };
  }




  // ──────────────────────────────────────────
  // Graph view
  // ──────────────────────────────────────────

  typeColor(type: GraphNode['type']): string {
    const map: Record<GraphNode['type'], string> = {
      component: 'var(--node-component)',
      failure_instance: 'var(--node-failure-instance)',
      failure_effect: 'var(--node-failure-effect)',
      system_effect: 'var(--node-system-effect)',
      safety_goal: 'var(--node-safety-goal)',
      safety_mechanism: 'var(--node-safety-mechanism)'
    };
    return map[type];
  }

  typeTextColor(type: GraphNode['type']): string {
    const map: Record<GraphNode['type'], string> = {
      component: 'var(--node-component-text)',
      failure_instance: 'var(--node-failure-instance-text)',
      failure_effect: 'var(--node-failure-effect-text)',
      system_effect: 'var(--node-system-effect-text)',
      safety_goal: 'var(--node-safety-goal-text)',
      safety_mechanism: 'var(--node-safety-mechanism-text)'
    };
    return map[type];
  }

  typeLabel(type: GraphNode['type']): string {
    const map: Record<GraphNode['type'], string> = {
      component: 'Component',
      failure_instance: 'FI',
      failure_effect: 'FE',
      system_effect: 'SE',
      safety_goal: 'SG',
      safety_mechanism: 'SM'
    };
    return map[type];
  }

  graphTypeDisplayName(type: GraphNode['type']): string {
    const map: Record<GraphNode['type'], string> = {
      component: 'Component',
      failure_instance: 'Failure Instance',
      failure_effect: 'Failure Effect',
      system_effect: 'System Effect',
      safety_goal: 'Safety Goal',
      safety_mechanism: 'Safety Mechanism'
    };
    return map[type];
  }

  private initGraph(): void {
    const container = this.graphContainerRef?.nativeElement;
    if (!container) return;

    // Clear any previous graph
    if (this.graphSimulation) {
      this.graphSimulation.stop();
      this.graphSimulation = null;
    }
    container.innerHTML = '';

    const { nodes, links } = this;
    if (nodes.length === 0) return;

    const relationMeta = this.graphLinkMode ? this.graphRelationMeta : null;
    const allowedSourceType = relationMeta?.sourceType ?? null;
    const allowedTargetType = relationMeta?.targetType ?? null;

    const width = container.clientWidth || 900;
    const height = container.clientHeight || 600;

    const svg = d3.select(container)
      .append('svg')
      .attr('width', '100%')
      .attr('height', '100%')
      .attr('viewBox', `0 0 ${width} ${height}`)
      .style('background', 'transparent');
    this.graphSvg = svg;

    // Arrowhead marker per relationship type
    const defs = svg.append('defs');
    const markerTypes = ['HAS_FAILURE', 'CAUSES', 'LEADS_TO', 'VIOLATES', 'COVERED_BY', 'COVERED_BY_LATENT'];
    for (const mt of markerTypes) {
      defs.append('marker')
        .attr('id', `arrow-${mt}`)
        .attr('viewBox', '0 -5 10 10')
        .attr('refX', 22)
        .attr('refY', 0)
        .attr('markerWidth', 6)
        .attr('markerHeight', 6)
        .attr('orient', 'auto')
        .append('path')
        .attr('d', 'M0,-5L10,0L0,5')
        .attr('fill', 'var(--text-muted)');
    }

    // Zoom/pan
    const zoomGroup = svg.append('g');
    const zoomBehavior = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.2, 4])
      .on('zoom', (event) => { zoomGroup.attr('transform', event.transform); });
    this.graphZoomBehavior = zoomBehavior;
    svg.call(zoomBehavior);

    const linkGroup = zoomGroup.append('g').attr('class', 'links');
    const nodeGroup = zoomGroup.append('g').attr('class', 'nodes');

    const link = linkGroup.selectAll('line')
      .data(links)
      .join('line')
      .attr('stroke', (d: GraphLink) => d.relType === 'COVERS_LATENT' ? 'var(--accent-orange)' : 'var(--text-muted)')
      .attr('stroke-width', 1.5)
      .attr('stroke-dasharray', (d: GraphLink) => d.relType === 'COVERS_LATENT' ? '5,3' : 'none')
      .attr('marker-end', (d: GraphLink) => `url(#arrow-${d.relType})`)
      .attr('opacity', 0.7);

    const node = nodeGroup.selectAll<SVGGElement, GraphNode>('g')
      .data(nodes)
      .join('g')
      .attr('class', 'node')
      .style('cursor', 'pointer');

    this.graphNodeSelection = node;

    node.append('circle')
      .attr('r', (d: GraphNode) => {
        if (!this.graphLinkMode && this.isFocusedComponentNode(d)) {
          return 18;
        }

        if (!this.graphLinkMode) {
          return 14;
        }

        if (this.graphLinkSource?.id === d.id) {
          return 22;
        }

        if (allowedSourceType && d.type === allowedSourceType) {
          return 18;
        }

        if (allowedTargetType && d.type === allowedTargetType) {
          return 18;
        }

        return 12;
      })
      .attr('fill', (d: GraphNode) => this.typeColor(d.type))
      .attr('stroke', (d: GraphNode) => {
        if (!this.graphLinkMode && this.isFocusedComponentNode(d)) {
          return 'var(--accent-blue)';
        }

        if (!this.graphLinkMode) {
          return 'var(--bg-graph)';
        }

        if (this.graphLinkSource?.id === d.id) {
          return 'var(--accent-orange)';
        }

        if (allowedSourceType && d.type === allowedSourceType) {
          return 'var(--accent-blue)';
        }

        if (allowedTargetType && d.type === allowedTargetType) {
          return 'var(--accent-green)';
        }

        return 'var(--border-color)';
      })
      .attr('stroke-width', (d: GraphNode) => {
        if (!this.graphLinkMode && this.isFocusedComponentNode(d)) {
          return 3;
        }

        if (this.graphLinkSource?.id === d.id) {
          return 3;
        }

        if (this.graphLinkMode && ((allowedSourceType && d.type === allowedSourceType) || (allowedTargetType && d.type === allowedTargetType))) {
          return 2;
        }

        return 1.5;
      })
      .attr('opacity', (d: GraphNode) => {
        if (!this.graphLinkMode && this.isFocusedComponentNode(d)) {
          return 1;
        }

        if (!this.graphLinkMode) {
          return 0.92;
        }

        if (this.graphLinkSource?.id === d.id) {
          return 1;
        }

        if ((allowedSourceType && d.type === allowedSourceType) || (allowedTargetType && d.type === allowedTargetType)) {
          return 1;
        }

        return 0.5;
      });

    // Type badge inside circle
    node.append('text')
      .attr('class', 'graph-node-type-badge')
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'central')
      .attr('fill', (d: GraphNode) => this.typeTextColor(d.type))
      .attr('font-size', '8px')
      .attr('font-weight', 'bold')
      .attr('pointer-events', 'none')
      .attr('opacity', (d: GraphNode) => {
        if (!this.graphLinkMode && this.isFocusedComponentNode(d)) {
          return 1;
        }

        if (!this.graphLinkMode) {
          return 1;
        }

        if ((allowedSourceType && d.type === allowedSourceType) || (allowedTargetType && d.type === allowedTargetType) || this.graphLinkSource?.id === d.id) {
          return 1;
        }

        return 0.55;
      })
      .text((d: GraphNode) => this.typeLabel(d.type));

    // Label below circle
    node.append('text')
      .attr('class', 'graph-node-label')
      .attr('text-anchor', 'middle')
      .attr('dy', 26)
      .attr('fill', 'var(--text-main)')
      .attr('font-size', '9px')
      .attr('pointer-events', 'none')
      .attr('opacity', (d: GraphNode) => {
        if (!this.graphLinkMode && this.isFocusedComponentNode(d)) {
          return 1;
        }

        if (!this.graphLinkMode) {
          return 1;
        }

        if ((allowedSourceType && d.type === allowedSourceType) || (allowedTargetType && d.type === allowedTargetType) || this.graphLinkSource?.id === d.id) {
          return 1;
        }

        return 0.45;
      })
      .text((d: GraphNode) => {
        const label = d.label;
        return label.length > 22 ? label.slice(0, 20) + '…' : label;
      });

    // Tooltip on hover
    const tooltip = d3.select(container)
      .append('div')
      .style('position', 'absolute')
      .style('background', 'var(--btn-secondary-bg)')
      .style('color', 'var(--text-main)')
      .style('padding', '6px 10px')
      .style('border-radius', '6px')
      .style('font-size', '11px')
      .style('pointer-events', 'none')
      .style('opacity', '0')
      .style('max-width', '240px')
      .style('border', '1px solid var(--border-color)');

    node
      .on('mouseover', (event: MouseEvent, d: GraphNode) => {
        tooltip
          .style('opacity', '1')
          .html(`<strong>${d.id}</strong><br/>${d.type.replace('_', ' ')}<br/><em>${d.label}</em>`);
      })
      .on('mousemove', (event: MouseEvent) => {
        const rect = container.getBoundingClientRect();
        tooltip
          .style('left', (event.clientX - rect.left + 14) + 'px')
          .style('top', (event.clientY - rect.top - 10) + 'px');
      })
      .on('mouseout', () => {
        tooltip.style('opacity', '0');
      })
      .on('click', (_event: MouseEvent, d: GraphNode) => {
        this.handleNodeClick(d, 'graph');
      })
      .on('contextmenu', (event: MouseEvent, d: GraphNode) => {
        this.requestComponentContextMenu(event, d);
      });

    // Drag
    const drag = d3.drag<SVGGElement, GraphNode>()
      .on('start', (event, d) => { if (!event.active) simulation.alphaTarget(0.3).restart(); d.fx = d.x; d.fy = d.y; })
      .on('drag', (event, d) => { d.fx = event.x; d.fy = event.y; })
      .on('end', (event, d) => { if (!event.active) simulation.alphaTarget(0); d.fx = null; d.fy = null; });

    node.call(drag);

    const simulation = d3.forceSimulation<GraphNode>(nodes)
      .force('link', d3.forceLink<GraphNode, GraphLink>(links).id((d) => d.id).distance(90).strength(0.4))
      .force('charge', d3.forceManyBody().strength(-280))
      .force(
        'laneX',
        d3.forceX<GraphNode>((d) => {
          const leftLaneX = Math.max(80, width * 0.12);
          const rightLaneX = Math.min(width - 80, width * 0.88);

          if (d.type === 'component') {
            return leftLaneX;
          }

          if (d.type === 'safety_goal') {
            return rightLaneX;
          }

          return width / 2;
        }).strength((d) => (d.type === 'component' || d.type === 'safety_goal' ? 0.8 : 0))
      )
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide(22))
      .on('tick', () => {
        link
          .attr('x1', (d) => (d.source as GraphNode).x ?? 0)
          .attr('y1', (d) => (d.source as GraphNode).y ?? 0)
          .attr('x2', (d) => (d.target as GraphNode).x ?? 0)
          .attr('y2', (d) => (d.target as GraphNode).y ?? 0);

        node.attr('transform', (d) => `translate(${d.x ?? 0},${d.y ?? 0})`);
      });

    this.graphSimulation = simulation;
    this.applyGraphNodeIndicators();
    this.scheduleFocusedComponentSync();
  }

  private applyGraphNodeIndicators(): void {
    if (!this.graphNodeSelection) {
      return;
    }

    const relationMeta = this.graphLinkMode ? this.graphRelationMeta : null;
    const allowedSourceType = relationMeta?.sourceType ?? null;
    const allowedTargetType = relationMeta?.targetType ?? null;
    const transitionDurationMs = 180;

    this.graphNodeSelection.select('circle')
      .transition()
      .duration(transitionDurationMs)
      .attr('r', (d: GraphNode) => {
        if (!this.graphLinkMode && this.isFocusedComponentNode(d)) {
          return 18;
        }

        if (!this.graphLinkMode) {
          return 14;
        }

        if (this.graphLinkSource?.id === d.id) {
          return 22;
        }

        if (allowedSourceType && d.type === allowedSourceType) {
          return 18;
        }

        if (allowedTargetType && d.type === allowedTargetType) {
          return 18;
        }

        return 12;
      })
      .attr('stroke', (d: GraphNode) => {
        if (!this.graphLinkMode && this.isFocusedComponentNode(d)) {
          return 'var(--accent-blue)';
        }

        if (!this.graphLinkMode) {
          return 'var(--bg-graph)';
        }

        if (this.graphLinkSource?.id === d.id) {
          return 'var(--accent-orange)';
        }

        if (allowedSourceType && d.type === allowedSourceType) {
          return 'var(--accent-blue)';
        }

        if (allowedTargetType && d.type === allowedTargetType) {
          return 'var(--accent-green)';
        }

        return 'var(--border-color)';
      })
      .attr('stroke-width', (d: GraphNode) => {
        if (!this.graphLinkMode && this.isFocusedComponentNode(d)) {
          return 3;
        }

        if (this.graphLinkSource?.id === d.id) {
          return 3;
        }

        if (this.graphLinkMode && ((allowedSourceType && d.type === allowedSourceType) || (allowedTargetType && d.type === allowedTargetType))) {
          return 2;
        }

        return 1.5;
      })
      .attr('opacity', (d: GraphNode) => {
        if (!this.graphLinkMode && this.isFocusedComponentNode(d)) {
          return 1;
        }

        if (!this.graphLinkMode) {
          return 0.92;
        }

        if (this.graphLinkSource?.id === d.id) {
          return 1;
        }

        if ((allowedSourceType && d.type === allowedSourceType) || (allowedTargetType && d.type === allowedTargetType)) {
          return 1;
        }

        return 0.5;
      });

    this.graphNodeSelection.select('.graph-node-type-badge')
      .transition()
      .duration(transitionDurationMs)
      .attr('opacity', (d: GraphNode) => {
        if (!this.graphLinkMode && this.isFocusedComponentNode(d)) {
          return 1;
        }

        if (!this.graphLinkMode) {
          return 1;
        }

        if ((allowedSourceType && d.type === allowedSourceType) || (allowedTargetType && d.type === allowedTargetType) || this.graphLinkSource?.id === d.id) {
          return 1;
        }

        return 0.55;
      });

    this.graphNodeSelection.select('.graph-node-label')
      .transition()
      .duration(transitionDurationMs)
      .attr('opacity', (d: GraphNode) => {
        if (!this.graphLinkMode && this.isFocusedComponentNode(d)) {
          return 1;
        }

        if (!this.graphLinkMode) {
          return 1;
        }

        if ((allowedSourceType && d.type === allowedSourceType) || (allowedTargetType && d.type === allowedTargetType) || this.graphLinkSource?.id === d.id) {
          return 1;
        }

        return 0.45;
      });

    this.focusGraphOnComponent();
  }

  private scheduleFocusedComponentSync(): void {
    if (this.graphFocusTimer !== null) {
      window.clearTimeout(this.graphFocusTimer);
    }

    this.graphFocusTimer = window.setTimeout(() => {
      this.graphFocusTimer = null;
      this.focusGraphOnComponent();
    }, 140);
  }

  private focusGraphOnComponent(): void {
    if (!this.focusedComponentDesignator) {
      return;
    }
    const focused = this.normalizeDesignator(this.focusedComponentDesignator);
    if (!focused || !this.graphNodeSelection || !this.graphSvg || !this.graphZoomBehavior) {
      return;
    }

    const matchingNodeSelection = this.graphNodeSelection
      .filter((d: GraphNode) => d.type === 'component' && this.normalizeDesignator(d.id) === focused);

    if (matchingNodeSelection.empty()) {
      return; // No matching node found, so we can't focus.
    }

    const matchingNode = matchingNodeSelection.datum() as GraphNode;

    if (typeof matchingNode.x !== 'number' || typeof matchingNode.y !== 'number') {
      return;
    }

    const container = this.graphContainerRef?.nativeElement;
    if (!container) {
      return;
    }

    const width = container.clientWidth || 900;
    const height = container.clientHeight || 600;
    const currentTransform = d3.zoomTransform(this.graphSvg.node());
    const scale = Math.max(currentTransform.k || 1, 1);
    const nextTransform = d3.zoomIdentity
      .translate(width / 2 - matchingNode.x * scale, height / 2 - matchingNode.y * scale)
      .scale(scale);

    this.graphSvg
      .transition()
      .duration(220)
      .call(this.graphZoomBehavior.transform, nextTransform);
  }

  private isFocusedComponentNode(node: GraphNode): boolean {
    if (!this.focusedComponentDesignator) {
      return false;
    }
    return !this.graphLinkMode && node.type === 'component' && this.normalizeDesignator(node.id) === this.normalizeDesignator(this.focusedComponentDesignator);
  }

  private normalizeDesignator(value: string): string {
    return value
      .trim()
      .toUpperCase()
      .replace(/\s+/g, '')
      .replace(/[^A-Z0-9]/g, '');
  }
}
